import { useRef, useEffect, useState } from 'react';
import type { Idea, DuplicateDetection, User } from '../api';
import * as api from '../api';
import { SourceIcon } from './StatusBadge';
import { Icon } from '../icons';
import { NeuralIcon } from './agent/NeuralIcon';
import { useFilters } from '../contexts/FilterContext';
import { CodeActivityHint } from './CodeActivityHint';

const BULK_STATUSES = [
  { value: 'captured', label: 'Captured' },
  { value: 'exploring', label: 'Exploring' },
  { value: 'building', label: 'Building' },
  { value: 'shipped', label: 'Shipped' },
] as const;

// PATCH /ideas/bulk caps ids at 50 per request
const BULK_CHUNK = 50;

const STATUS_LABEL: Record<string, string> = {
  captured: 'Captured', exploring: 'Exploring', building: 'Building', shipped: 'Shipped', shelved: 'Shelved',
};

/** The body's first line of prose, without Markdown markers, for the one-line preview. */
function previewLine(body: string | null): string {
  if (!body) return '';
  for (const raw of body.split('\n')) {
    const line = raw
      .replace(/^\s{0,3}(#{1,6}\s+|[-*+]\s+|\d+\.\s+|>\s?)/, '')
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/[*_`~]+/g, '')
      .trim();
    if (line && !line.startsWith('```')) return line;
  }
  return '';
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function isRecentlyCreated(iso: string): boolean {
  const d = new Date(iso + 'Z');
  return Date.now() - d.getTime() < 120_000; // within 2 minutes
}

function formatTime(iso: string): string {
  const d = new Date(iso + 'Z');
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ScoreBadge({ score }: { score: number | null | undefined }) {
  if (score == null) return null;
  const pct = (score * 100).toFixed(0);
  let className = 'score-badge score-blue';
  if (score > 0.9) className = 'score-badge score-green';
  else if (score > 0.7) className = 'score-badge score-amber';
  return <span className={className}>{pct}%</span>;
}

export function IdeasView({
  ideas,
  loading,
  error,
  selectedId,
  onSelect,
  onDoubleClick,
  duplicates,
  onDismissDuplicate,
  onMergeDuplicate,
  hasMore,
  loadingMore,
  onLoadMore,
  searchType,
  semanticLimitMessage,
  user,
  onBulkChanged,
  onCapture,
}: {
  ideas: Idea[];
  loading: boolean;
  error: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDoubleClick: (id: number) => void;
  duplicates: DuplicateDetection[];
  onDismissDuplicate: (id: number) => void;
  onMergeDuplicate: (id: number) => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  searchType?: string | null;
  semanticLimitMessage?: string | null;
  user?: User | null;
  onBulkChanged?: () => void;
  onCapture?: () => void;
}) {
  const filters = useFilters();

  // Bulk selection
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkTag, setBulkTag] = useState('');

  // Drop selections for ideas that left the list (filter change, archive, pagination reset)
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(ideas.map((i) => i.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [ideas]);

  const toggleSelected = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(new Set());
    setBulkError(null);
    setBulkTag('');
  };

  const runBulk = async (fn: (ids: number[]) => Promise<void>) => {
    const ids = [...selected];
    setBulkBusy(true);
    setBulkError(null);
    try {
      await fn(ids);
      clearSelection();
      onBulkChanged?.();
    } catch (err) {
      setBulkError((err as Error).message);
      onBulkChanged?.();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkMove = (status: string) =>
    runBulk(async (ids) => {
      for (const batch of chunk(ids, BULK_CHUNK)) {
        await api.bulkUpdateIdeas({ ids: batch, status });
      }
    });

  const bulkTagAction = (mode: 'add' | 'remove') => {
    const name = bulkTag.trim().toLowerCase();
    if (!name) return;
    return runBulk(async (ids) => {
      for (const batch of chunk(ids, BULK_CHUNK)) {
        await api.bulkUpdateIdeas(
          mode === 'add' ? { ids: batch, add_tags: [name] } : { ids: batch, remove_tags: [name] }
        );
      }
    });
  };

  const bulkArchive = () => {
    if (!window.confirm(`Archive ${selected.size} idea${selected.size !== 1 ? 's' : ''}?`)) return;
    return runBulk(async (ids) => {
      for (const id of ids) {
        await api.archiveIdea(id);
      }
    });
  };

  // Build a map of idea_id -> duplicates for inline display
  const dupsByIdeaId = new Map<number, DuplicateDetection[]>();
  for (const dup of duplicates) {
    const existing = dupsByIdeaId.get(dup.idea_id) ?? [];
    existing.push(dup);
    dupsByIdeaId.set(dup.idea_id, existing);
  }

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const loadMore = onLoadMore;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  const searchSummary = (() => {
    if (!filters.searchQuery.trim() || !searchType) return null;
    const count = ideas.length;
    let typeLabel: string;
    let modeLabel = '';
    if (searchType === 'semantic') {
      typeLabel = 'semantic';
    } else {
      typeLabel = 'keyword';
      modeLabel = ` (${filters.ftsMode === 'or' ? 'any word' : filters.ftsMode === 'and' ? 'all words' : 'exact'})`;
    }
    return `${count} result${count !== 1 ? 's' : ''} · ${typeLabel}${modeLabel}`;
  })();

  const atLimit = user?.plan === 'free' && !user.is_trialing && user.active_idea_count >= 50;

  return (
    <div className="inbox-view">
      <div className="idea-list">
        {atLimit && (
          <div className="limit-banner">
            <div>
              <div className="limit-banner-title">You've reached the free plan limit</div>
              <div className="limit-banner-desc">
                Upgrade to Pro for unlimited ideas, semantic search, Claude MCP, CLI access, and more.
              </div>
            </div>
            <a href="/upgrade" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>Upgrade to Pro</a>
          </div>
        )}

        {searchSummary && !loading && (
          <div className="search-summary">{searchSummary}</div>
        )}

        {selected.size > 0 && (
          <div className="bulk-toolbar">
            <span className="bulk-count">{selected.size} selected</span>
            <select
              className="bulk-select"
              value=""
              disabled={bulkBusy}
              onChange={(e) => { if (e.target.value) bulkMove(e.target.value); }}
            >
              <option value="" disabled>Move to…</option>
              {BULK_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <span className="bulk-tag-group">
              <input
                className="bulk-tag-input mono"
                value={bulkTag}
                disabled={bulkBusy}
                onChange={(e) => setBulkTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); bulkTagAction('add'); } }}
                placeholder="tag"
              />
              <button className="btn btn-ghost-sm" disabled={bulkBusy || !bulkTag.trim()} onClick={() => bulkTagAction('add')} title="Add tag to selected">+ tag</button>
              <button className="btn btn-ghost-sm" disabled={bulkBusy || !bulkTag.trim()} onClick={() => bulkTagAction('remove')} title="Remove tag from selected">− tag</button>
            </span>
            <button className="btn btn-ghost-sm bulk-archive" disabled={bulkBusy} onClick={bulkArchive}>Archive</button>
            <button className="btn btn-ghost-sm" disabled={bulkBusy} onClick={clearSelection}>Clear</button>
            {bulkBusy && <span className="spinner spinner-sm" />}
            {bulkError && <span className="bulk-error">{bulkError}</span>}
          </div>
        )}

        {semanticLimitMessage && !loading && (
          <div className="semantic-limit-notice">
            {semanticLimitMessage}{' '}
            <a href="/upgrade">Upgrade to Pro</a>
          </div>
        )}

        {loading && ideas.length === 0 && (
          <div className="empty-state">
            <div className="spinner" />
            <p>Loading ideas...</p>
          </div>
        )}

        {error && (
          <div className="empty-state error-state">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && ideas.length === 0 && (() => {
          const hasFilters = filters.searchQuery.trim() || filters.selectedStatuses.length > 0 || filters.selectedTags.length > 0;
          if (!hasFilters) {
            return (
              <div className="empty-state empty-state-compact">
                <p>No ideas yet.</p>
                <div className="empty-state-actions">
                  {onCapture && <button className="btn btn-primary" onClick={onCapture}>Capture</button>}
                  <a className="btn btn-secondary" href="/app/start">Connect a channel</a>
                </div>
              </div>
            );
          }
          return (
            <div className="empty-state">
              <div className="empty-icon"><NeuralIcon size={36} /></div>
              <h3>No ideas found</h3>
              <p>Try adjusting your filters or click "+ Capture" to save a new idea.</p>
            </div>
          );
        })()}

        <div className="ir-list" role="list">
        {ideas.map((idea) => {
          const tags = idea.tags ?? [];
          const preview = previewLine(idea.body);
          const fresh = isRecentlyCreated(idea.created_at);
          return (
          <div key={idea.id} role="listitem" className="ir-item">
            <div className={`ir-wrap${selected.size > 0 ? ' ir-wrap-selecting' : ''}`}>
            <button
              className={`ir${selectedId === idea.id ? ' ir-selected' : ''}${selected.has(idea.id) ? ' ir-checked' : ''}${fresh ? ' ir-fresh' : ''}`}
              onClick={() => onSelect(idea.id)}
              onDoubleClick={() => onDoubleClick(idea.id)}
              aria-current={selectedId === idea.id ? 'true' : undefined}
            >
              <span className="ir-title">
                <span className="ir-num">#{idea.number}</span>
                {idea.title}
              </span>
              <span className="ir-meta">
                <ScoreBadge score={idea.score} />
                <span className="ir-dot" style={{ background: `var(--nr-status-${idea.status})` }} aria-hidden="true" />
                {STATUS_LABEL[idea.status] ?? idea.status}
                <span className="ir-date">{formatTime(idea.created_at)}</span>
              </span>
              <span className="ir-preview">
                {!idea.vectorize_id && fresh ? <span className="ir-tagging">Tagging…</span> : preview}
              </span>
              <span className="ir-tags">
                <SourceIcon source={idea.source} />
                {tags.slice(0, 3).join('  ')}
                {tags.length > 3 && `  +${tags.length - 3}`}
              </span>
              <CodeActivityHint activity={idea.code_activity} className="ir-code" />
            </button>
            <label className="idea-check" title="Select for bulk actions">
              <input
                type="checkbox"
                aria-label={`Select #${idea.number}`}
                checked={selected.has(idea.id)}
                onChange={() => toggleSelected(idea.id)}
              />
            </label>
            </div>

            {/* Duplicate hint (D-84 I2: the full comparison lives in the detail panel and Triage) */}
            {dupsByIdeaId.get(idea.id)?.map((dup) => (
              <div key={dup.id} className="ir-dup">
                <Icon name="dupes" size={12} />
                <span className="ir-dup-text">Looks like #{dup.duplicate_number} {dup.duplicate_title}</span>
                <button className="ir-dup-btn" onClick={(e) => { e.stopPropagation(); onMergeDuplicate(dup.id); }}>Merge</button>
                <button className="ir-dup-btn" onClick={(e) => { e.stopPropagation(); onDismissDuplicate(dup.id); }}>Not a duplicate</button>
              </div>
            ))}
          </div>
          );
        })}
        </div>

        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
        {loadingMore && (
          <div className="empty-state" style={{ padding: '1rem' }}>
            <div className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
}
